import React from 'react';
import { Buffer } from 'buffer';
import { Api, LOGGER_CACHE_DATA_KEY } from 'src/common';
import { getUserId, setSessionStartTime } from 'src/services/user.service';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import { v4 as uuidv4 } from 'uuid';
import { AppCacheKeys } from 'src/types';
import { LoggingService } from 'src/features/common/services/logging-service';
import { getBrowserName, getOSName } from 'src/helpers/browser';
import { Environment } from 'src/Environment';
import { journeyName } from 'src/utils/SessionStorage/GetJourneyNameFromSessionStorage';
import { getLoggingTraceId } from 'src/helpers/logging';

interface Broker {
  remotePaymentsEnabled: boolean;
  name: string;
  enabled: boolean;
  code: string;
}

interface IChangePassword {
  newPassword: string;
  oldPassword: string; // Not actually the old password, it's the OTP
}

type SalesOrg = {
  name: string;
  code: string;
};

interface IInitState {
  isValidBroker: boolean | null;
  isLoading: boolean;
  broker: Broker | undefined;
  salesOrganizations: SalesOrg[];
  hasDealerId: boolean;
}

enum API_URLS {
  ResetPassWord = 'broker/reset-password',
  ChangePassword = 'change-password',
  ValidateOrg = 'salesorganizations',
}

enum SessionKeys {
  Broker = 'broker',
  UserIdKey = 'smme_portal.userId',
  MerchantIdKey = 'smme_portal.userName',
  LoggedInKey = 'smme_portal.loggedIn',
  LoginType = 'smme_portal.logInType',
  SalesOrg = 'sales_org',
  AgentJourney = 'agent_journey',
}

interface IState {
  isValidBroker: boolean | null;
  isLoading: boolean;
  broker?: Broker;
  salesOrganizations: SalesOrg[];
  hasDealerId: boolean;
  getSalesOrgs: () => void;
  validateBroker: (salesOrg: string) => void;
  validateBrokerAgainstSalesOrg: (
    salesOrg: string,
    agentId: string,
    userReference: string,
  ) => Promise<unknown>;
  resetPassword: (merchantId: string, abortController: AbortController) => Promise<object>;
  changePassword: (
    { newPassword, oldPassword }: IChangePassword,
    abortController: AbortController,
  ) => Promise<object>;
  authenticate: ({ merchantId, password }: { merchantId: string; password: string }) => Promise<{
    ok: boolean;
    message: string;
    isTechnical: boolean;
    userId?: string;
    firstTimeLogin?: boolean;
    error?: string | null;
  }>;
  agentLogin: ({ agentId, password }: { agentId: string; password: string }) => Promise<{
    ok: boolean;
    message: string;
    isTechnical: boolean;
    userId?: string;
    firstTimeLogin?: boolean;
    error?: null | string;
  }>;
  compareSalesOrgs: (orgOne: SalesOrg, orgTwo: SalesOrg) => 0 | 1 | -1;
}

enum ActionType {
  IS_VALID_BROKER = 'SET_VALID_BROKER',
  SET_LOADING = 'SET_IS_LOADING',
  BROKER = 'SET_BROKER',
  SALES_ORG = 'SET_ORGS',
  DEALER_ID_PROVIDED = 'HAS_DEALER',
}

type IsValidBroker = {
  type: ActionType.IS_VALID_BROKER;
  isValidBroker: boolean | null;
};

type SetIsLoading = {
  type: ActionType.SET_LOADING;
  isLoading: boolean;
};

type SetBroker = {
  type: ActionType.BROKER;
  broker: Broker;
};

type SetSalesOrg = {
  type: ActionType.SALES_ORG;
  salesOrganizations: SalesOrg[];
};

type SetHasDealerId = {
  type: ActionType.DEALER_ID_PROVIDED;
  hasDealerId: boolean;
};

type Props = {
  children: React.ReactNode;
};

type Action = IsValidBroker | SetIsLoading | SetBroker | SetSalesOrg | SetHasDealerId;

type SalesOrganization = {
  name: string;
  code: string;
};

type SalesOrgResponse = {
  ok: boolean;
  status: number;
  salesOrganizations: SalesOrganization[];
  message: string;
};

export const BrokerLoginContext = React.createContext<IState | undefined>(undefined);

export const BrokerLoginProvider: React.FC<Props> = ({ children }) => {
  const loggingService = new LoggingService();
  const handleTechnicalError = useTechnicalErrorHandler();

  const initState: IInitState = {
    isValidBroker: null,
    isLoading: false,
    broker: undefined,
    salesOrganizations: [],
    hasDealerId: true,
  };

  /**
   * logging cache
   * generate a new traceId if there's no one generated or I come to broker login from a different app
   */
  const BROKER_LOGIN = 'broker-login';
  let loggerCacheData = JSON.parse(sessionStorage.getItem(LOGGER_CACHE_DATA_KEY) || '{}');
  if (Object.keys(loggerCacheData).length === 0 || loggerCacheData.app !== BROKER_LOGIN) {
    loggerCacheData = {
      traceId: uuidv4(),
      app: BROKER_LOGIN,
    };
    sessionStorage.setItem(LOGGER_CACHE_DATA_KEY, JSON.stringify(loggerCacheData));
  }

  const handleBrokerError = (message: string) => {
    // @ts-ignore
    handleTechnicalError(message, false, true);
  };

  const reducer = (state: IInitState, action: Action): IInitState => {
    switch (action.type) {
      case ActionType.IS_VALID_BROKER:
        return {
          ...state,
          isValidBroker: action.isValidBroker,
        };

      case ActionType.DEALER_ID_PROVIDED:
        return {
          ...state,
          hasDealerId: action.hasDealerId,
        };

      case ActionType.SALES_ORG:
        return {
          ...state,
          salesOrganizations: action.salesOrganizations,
        };
      case ActionType.SET_LOADING:
        return {
          ...state,
          isLoading: action.isLoading,
        };
      case ActionType.BROKER:
        return {
          ...state,
          broker: action.broker,
        };
      default:
        return state;
    }
  };

  const getSalesOrgs = async (dispatcher: React.Dispatch<Action>) => {
    try {
      const startTime = performance.now();
      const { response, status, url } = await Api.get<SalesOrgResponse>('salesorganizations', {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const responseTime = performance.now() - startTime;

      loggingService.info({
        traceId: getLoggingTraceId(),
        solutionType: undefined,
        journey: journeyName.THIRD_PARTY,
        currentStep: {
          subStepName: 'Login',
        },
        referenceNumber: undefined,
        timestamp: new Date().toISOString(),
        api: {
          responseTime: responseTime.toFixed(),
          statusCode: status,
          endpoint: url,
          responseBody: typeof response === 'object' ? response : { result: response },
        },
        device: {
          platform: getOSName(navigator.userAgent),
          browserName: getBrowserName(navigator.userAgent),
        },
        url: window.location.href,
        environment: Environment.current.env,
      });

      if (!response.ok) {
        handleBrokerError(response?.message || 'Failed to get sales org');
        return;
      }

      dispatcher({
        type: ActionType.SALES_ORG,
        salesOrganizations: response?.salesOrganizations || [],
      });

      dispatcher({
        type: ActionType.DEALER_ID_PROVIDED,
        hasDealerId: false,
      });
    } catch (error) {
      console.error(error);
      dispatcher({
        type: ActionType.SALES_ORG,
        salesOrganizations: [],
      });
    }
  };

  const changePassword = async (
    { oldPassword, newPassword }: IChangePassword,
    abortController: AbortController,
  ) => {
    let isTechnical = false;
    try {
      const userId = getUserId();

      if (!userId || !oldPassword || !newPassword) {
        handleBrokerError('User ID, otp or password null');
      }

      const { response } = await Api.post<{
        ok: boolean;
        status: number;
        result: {
          ok: boolean;
          message: string;
          isTechnical: boolean;
          error: string;
        };
      }>(API_URLS.ChangePassword, {
        body: JSON.stringify({
          userId: userId,
          oldPassword: Buffer.from(oldPassword, 'binary').toString('base64'),
          newPassword: Buffer.from(newPassword, 'binary').toString('base64'),
        }),
        headers: {
          'Content-Type': 'application/json',
        },
        signal: abortController?.signal,
      });

      if (!response.ok) {
        isTechnical = response.status >= 500;
      }

      return response.result;
    } catch (error) {
      // @ts-ignore
      const message = error?.message;
      return {
        ok: false,
        message: message,
        isTechnical: isTechnical,
        error: message,
      };
    }
  };

  const authenticate = async (merchantId: string, password: string) => {
    let isTechnical = false;

    const body = {
      channel: 'WEB',
      username: merchantId,
      password: Buffer.from(password, 'binary').toString('base64'),
      merchantEnrollment: true,
    };

    const startTime = performance.now();
    try {
      const { response, status, url } = await Api.post<{
        ok: boolean;
        status: number;
        result: {
          userId: string;
          resultMessage: string;
          privileges: { name: string }[];
          message: string;
        };
      }>('login', {
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const responseTime = performance.now() - startTime;

      loggingService.info({
        traceId: getLoggingTraceId(),
        solutionType: undefined,
        journey: journeyName.THIRD_PARTY,
        currentStep: {
          subStepName: 'Login',
        },
        referenceNumber: undefined,
        timestamp: new Date().toISOString(),
        api: {
          responseTime: responseTime.toFixed(),
          statusCode: status,
          endpoint: url,
          responseBody: typeof response === 'object' ? response : { result: response },
          requestBody: {
            ...body,
            password: undefined,
          },
        },
        device: {
          platform: getOSName(navigator.userAgent),
          browserName: getBrowserName(navigator.userAgent),
        },
        url: window.location.href,
        environment: Environment.current.env,
      });

      if (!response.ok) {
        isTechnical = response.status >= 500;
        throw new Error(`Broker Login Context revamp: ${response.result?.message}`);
      }

      sessionStorage.setItem(SessionKeys.LoggedInKey, JSON.stringify(true));
      sessionStorage.setItem(SessionKeys.LoginType, response.result.resultMessage);
      sessionStorage.setItem(SessionKeys.UserIdKey, response.result.userId);
      sessionStorage.setItem(SessionKeys.MerchantIdKey, merchantId.trimEnd());
      setSessionStartTime(new Date());

      return {
        ok: true,
        message: '',
        isTechnical: false,
        userId: response.result?.userId,
        firstTimeLogin: response.result.resultMessage === 'CHANGE_PASSWORD',
        error: null,
      };
    } catch (error) {
      // @ts-ignore
      const message = error?.message;
      return {
        ok: false,
        message: message,
        isTechnical: isTechnical,
        error: message,
      };
    }
  };

  const agentLogin = async (agentId: string, password: string) => {
    let isTechnical = false;
    try {
      const { response } = await Api.post<{
        ok: boolean;
        status: number;
        fault: {
          message: string;
        };
        result: {
          userName: string;
          message: string;
        };
      }>('auth', {
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: agentId, password: password }),
      });

      if (!response.ok) {
        isTechnical = response.status >= 500;
        throw new Error(
          `Broker Login Context revamp: ${response.result?.message ?? response?.fault.message}`,
        );
      }

      sessionStorage.setItem(SessionKeys.AgentJourney, JSON.stringify(true));
      sessionStorage.setItem(SessionKeys.MerchantIdKey, agentId);
      setSessionStartTime(new Date());

      return {
        ok: true,
        message: '',
        isTechnical: false,
        userId: response.result?.userName,
        firstTimeLogin: false,
        error: null,
      };
    } catch (error) {
      // @ts-ignore
      const message = error?.message;
      return {
        ok: false,
        message: message,
        isTechnical: isTechnical,
        error: message,
      };
    }
  };

  const resetPassword = async (merchantId: string, abortController: AbortController) => {
    let isTechnical = false;
    try {
      const { response } = await Api.post<{
        ok: boolean;
        status: number;
        result: { responseCode: number; success: boolean; message: string };
      }>(API_URLS.ResetPassWord, {
        body: JSON.stringify({
          userName: merchantId,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
        signal: abortController.signal,
      });

      if (!response.ok) {
        isTechnical = response.status >= 500;

        throw new Error(
          `Broker Login Context revamp: ${response.result?.message || 'Failed to reset password'}`,
        );
      }

      const body = response.result || {};

      // sessionStorage.setItem(SessionKeys.UserIdKey, body.userId);
      sessionStorage.setItem(SessionKeys.MerchantIdKey, merchantId);

      return body;
    } catch (error) {
      // @ts-ignore
      const message = error?.message;
      return {
        ok: false,
        message: message,
        isTechnical: isTechnical,
        error: message,
      };
    }
  };

  const validateBrokerAgainstSalesOrg = async (
    salesOrg: string,
    agentId: string,
    userReference: string,
  ) => {
    try {
      const { response } = await Api.get<{
        status: number;
        ok: boolean;
        data: {
          reasonCode: string;
          message: string;
          reasonDescription: string;
        };
      }>(`salesagents/${agentId}/salesorganization/${salesOrg}/${userReference}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return response.data.reasonDescription;
      }
    } catch (error) {
      // @ts-ignore
      return error;
    }
  };

  const validateBroker = async (salesOrg: string, dispatcher: React.Dispatch<Action>) => {
    try {
      dispatcher({
        type: ActionType.SET_LOADING,
        isLoading: true,
      });

      const startTime = performance.now();
      const { response, status, url } = await Api.get<{
        ok: boolean;
        status: number;
        result: {
          remotePaymentsEnabled: boolean;
          reasonDescription: string;
          name: string;
          enabled: boolean;
          code: string;
        };
      }>(`${API_URLS.ValidateOrg}/${salesOrg}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const responseTime = performance.now() - startTime;

      loggingService.info({
        traceId: getLoggingTraceId(),
        solutionType: undefined,
        journey: journeyName.THIRD_PARTY,
        currentStep: {
          subStepName: 'Login',
        },
        referenceNumber: undefined,
        timestamp: new Date().toISOString(),
        api: {
          responseTime: responseTime.toFixed(),
          statusCode: status,
          endpoint: url,
          responseBody: typeof response === 'object' ? response : { result: response },
        },
        device: {
          platform: getOSName(navigator.userAgent),
          browserName: getBrowserName(navigator.userAgent),
        },
        url: window.location.href,
        environment: Environment.current.env,
      });

      if (!response.ok) {
        toastMessageServiceSingleton.addError(
          'This sales organisation was not found. Please use your assigned sales organisation site.',
          null,
          '404',
          true,
          null,
          true,
        );

        dispatcher({
          type: ActionType.IS_VALID_BROKER,
          isValidBroker: false,
        });
        return;
      }

      dispatcher({
        type: ActionType.IS_VALID_BROKER,
        isValidBroker: true,
      });
      dispatcher({
        type: ActionType.BROKER,
        broker: response.result,
      });

      const currentBroker = sessionStorage.getItem(SessionKeys.SalesOrg);

      if (currentBroker !== response.result.code) {
        sessionStorage.setItem(AppCacheKeys.RestartApp, 'true');
      }

      sessionStorage.setItem(SessionKeys.SalesOrg, response.result.code);
      sessionStorage.setItem(SessionKeys.Broker, JSON.stringify(response.result));
    } catch (error) {
      dispatcher({
        type: ActionType.IS_VALID_BROKER,
        isValidBroker: false,
      });
    } finally {
      dispatcher({
        type: ActionType.SET_LOADING,
        isLoading: false,
      });
    }
  };

  const compareSalesOrgs = (orgOne: SalesOrg, orgTwo: SalesOrg) => {
    const nameOne = orgOne.name.toUpperCase();
    const nameTwo = orgTwo.name.toUpperCase();
    if (nameOne < nameTwo) {
      return -1;
    }
    if (nameOne > nameTwo) {
      return 1;
    }

    return 0;
  };

  const [brokerLoginState, dispatch] = React.useReducer(reducer, initState);

  const brokerContext = React.useMemo(
    () => ({
      validateBroker: (salesOrg: string) => validateBroker(salesOrg, dispatch),
      validateBrokerAgainstSalesOrg: async (
        salesOrg: string,
        agentId: string,
        userReference: string,
      ) => validateBrokerAgainstSalesOrg(salesOrg, agentId, userReference),
      resetPassword: async (merchantId: string, abortController: AbortController) =>
        resetPassword(merchantId, abortController),
      changePassword: async (passwords: IChangePassword, abortController: AbortController) =>
        changePassword(passwords, abortController),
      authenticate: async (credentials: { merchantId: string; password: string }) =>
        authenticate(credentials.merchantId, credentials.password),
      getSalesOrgs: async () => getSalesOrgs(dispatch),
      agentLogin: async ({ agentId, password }: { agentId: string; password: string }) =>
        agentLogin(agentId, password),
      compareSalesOrgs: (orgOne: SalesOrg, orgTwo: SalesOrg) => compareSalesOrgs(orgOne, orgTwo),
    }),
    [],
  );

  return (
    <BrokerLoginContext.Provider value={{ ...brokerContext, ...brokerLoginState }}>
      {children}
    </BrokerLoginContext.Provider>
  );
};
